import React from 'react';
import {observer} from 'mobx-react';
import TableWrapper from './TableWrapper';
import ReportGenerationModal from './ReportGenerationModal';
import CellDropdownList from './CellDropdownList';
import InsightsModalCellAttributeDrilldown from './InsightsModalCellAttributeDrilldown';
import ColumnHeaderFilterMenu from './ColumnHeaderFilterMenu';
import {stylesheet} from 'typestyle';
import {AnalyticsTab, DiagramMode, Store} from '../../stores/store';
import {I18n} from '../../i18n/i18n';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import DataSourceSelectorFoldout from './DataSourceSelectorFoldout';
import TableOptionMenu from './TableOptionMenu';
import {GreyColor, Shadow8, WhiteColor} from '../../styles/colors';

interface SelectorTableProps {
    store: Store;
}

interface SelectorTableState {
    columnSelectorIsVisible: boolean;
    showDataSourceSelector: boolean;
    showMenu: boolean;
}

@observer
export default class SelectorTable extends React.Component<SelectorTableProps, SelectorTableState> {
    public state = {
        columnSelectorIsVisible: false,
        showDataSourceSelector: false,
        showMenu: false,
    };

    private renderChannelOrTableColorLegend() {
        const {channelStore, compareStore} = this.props.store;
        const {compareData, getChannelColor, compareIsEnabled} = compareStore;
        const {channelCompare, variantChannelCompare} = compareData;
        const selectedChannelName = channelStore.selectedChannel ? channelStore.selectedChannel.name : '';
        if (!compareIsEnabled) {
            return <React.Fragment>{selectedChannelName}</React.Fragment>;
        }

        const compareTargets = ((): string[] => {
            if (channelCompare) {
                return channelCompare.channelNames;
            } else if (variantChannelCompare) {
                return variantChannelCompare.variantIds;
            }
            return [];
        })();

        const channelAndAbtestName = variantChannelCompare ? (
            <React.Fragment>
                <div className={tableCss.channelColorLegendItem}>
                    <div className={tableCss.channelColorLegendChannelName}>{selectedChannelName}</div>
                </div>
                <div className={tableCss.channelColorLegendItem}>
                    <div className={tableCss.channelColorLegendChannelName}>
                        {channelStore.getNameForAbtestId(
                            variantChannelCompare.testId,
                            channelStore.selectedChannel ? channelStore.selectedChannel.name : ''
                        ) || variantChannelCompare.testId}
                    </div>
                </div>
            </React.Fragment>
        ) : null;

        return (
            <React.Fragment>
                {channelAndAbtestName}
                {compareTargets.map((target) => (
                    <div className={tableCss.channelColorLegendItem} key={target}>
                        <div
                            className={tableCss.channelColorLegendBox}
                            style={{
                                backgroundColor: getChannelColor(target, compareTargets).toString(),
                            }}
                        />
                        <div className={tableCss.channelColorLegendChannelName}>{target}</div>
                    </div>
                ))}
            </React.Fragment>
        );
    }

    private renderCompareTargetCurrencySelectorIfNecessary() {
        const {compareIsEnabled, comparisonTargetCurrency, setAndApplyNewCompareTargetCurrency} = this.props.store.compareStore;
        const {getAllChannelCurrencies} = this.props.store.channelStore;
        if (compareIsEnabled) {
            return (
                <div className={tableCss.rightItemContainer}>
                    <select
                        className={tableCss.currencySelector}
                        value={comparisonTargetCurrency}
                        onChange={(e) => setAndApplyNewCompareTargetCurrency(e.currentTarget.value)}>
                        {getAllChannelCurrencies().map((currency) => (
                            <option key={currency} value={currency}>
                                {currency}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }
        return null;
    }

    private changeSelectedChannel = (channelName: string) => {
        this.props.store.channelStore.setSelectedChannel(channelName);
        this.props.store.selectorTableStore.recomputeMultiGridSizeIfPossible();
    };

    private changeComparedChannel = (compareChannelNames: string[]) => {
        if (compareChannelNames.length > 1) {
            this.props.store.compareStore.applyChannelCompareSettings(compareChannelNames);
        } else {
            this.props.store.compareStore.applyChannelCompareSettings([]);
        }
    };

    private renderDataSourceSelectorFoldout() {
        const {diagramMode, channelStore, compareStore, activeAnalyticsTab} = this.props.store;
        if (!this.state.showDataSourceSelector) {
            return null;
        }
        return (
            <DataSourceSelectorFoldout
                dropdown={diagramMode === DiagramMode.None}
                disableComparison={activeAnalyticsTab === AnalyticsTab.CampaignView}
                changeSelectedChannel={this.changeSelectedChannel}
                changeComparedChannel={this.changeComparedChannel}
                availableChannels={channelStore.availableChannels}
                selectedChannel={channelStore.selectedChannel}
                closeTableDataSourceSelector={() => this.setState({showDataSourceSelector: false})}
                compareData={compareStore.compareData}
                abTestsForChannels={channelStore.abTestsForChannels}
                applyAbtestCompareSettings={compareStore.applyAbtestCompareSettings}
                importAbTest={compareStore.importAbTest}
                importingAbTests={compareStore.importingAbTests}
            />
        );
    }

    private renderChannelSelectorTitle(): React.ReactNode {
        const {
            compareIsEnabled,
            compareData: {channelCompare, variantChannelCompare},
        } = this.props.store.compareStore;

        if (compareIsEnabled) {
            if (channelCompare) {
                return (
                    <span className={tableCss.tableTitleText}>
                        <I18n i18nKey="table:channelComparison" />
                    </span>
                );
            }
            if (variantChannelCompare) {
                return (
                    <span className={tableCss.tableTitleText}>
                        <I18n i18nKey="table:channelVariantComparison" />
                    </span>
                );
            }
        }
        return (
            <span className={tableCss.tableTitleText}>
                <I18n i18nKey="table:singleChannel" />
            </span>
        );
    }

    private renderChannelSelector() {
        return (
            <div className={tableCss.dataSourceSelector}>
                {this.renderDataSourceSelectorFoldout()}
                <div className={tableCss.dataSourceOpener} onClick={() => this.setState({showDataSourceSelector: true})}>
                    <FontAwesomeIcon icon="cog" />
                    {this.renderChannelSelectorTitle()}
                </div>
            </div>
        );
    }

    private renderHeader() {
        const {diagramMode, lineDiagramStore, compareStore, activeAnalyticsTab, timeIntervalStore, channelStore, odtExportStore} = this.props.store;

        return (
            <div className={tableCss.headerContainer}>
                <div className={tableCss.headerContainerInner}>
                    <div className={tableCss.channelSelectorColorLegendContainer}>
                        {this.renderChannelSelector()}
                        <div className={tableCss.colorLegend}>{this.renderChannelOrTableColorLegend()}</div>
                    </div>
                    {this.renderCompareTargetCurrencySelectorIfNecessary()}
                    <div className={tableCss.rightItemContainer}>
                        <button className={tableCss.menuButton} onClick={() => this.setState({showMenu: true})}>
                            <FontAwesomeIcon icon="bars" />
                        </button>
                        {this.state.showMenu ? (
                            <TableOptionMenu
                                diagramMode={diagramMode}
                                odtExportStore={odtExportStore}
                                selectedChannel={channelStore.selectedChannel}
                                disableCSVExport={compareStore.compareIsEnabled || activeAnalyticsTab === AnalyticsTab.CampaignView}
                                compareEnabled={compareStore.compareIsEnabled}
                                mainDateRange={timeIntervalStore.mainDateRange}
                                analyticsTab={activeAnalyticsTab}
                                onClose={() => this.setState({showMenu: false})}
                                disableAddToReportButton={
                                    (lineDiagramStore.requestedNormalDiagrams.length === 0 &&
                                        lineDiagramStore.requestedChannelDiagrams.length === 0) ||
                                    compareStore.compareIsEnabled ||
                                    activeAnalyticsTab === AnalyticsTab.CampaignView
                                }
                                generateTableDataCsvFormatSemicolon={this.props.store.selectorTableStore.generateTableDataCsvSemicolonFormat}
                                generateTableDataCsvFormatComma={this.props.store.selectorTableStore.generateTableDataCsvCommaFormat}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }

    public render() {
        const onColumnSelectorVisible = (isVisible: boolean) => {
            this.setState({columnSelectorIsVisible: isVisible});
        };

        return (
            <div className={tableCss.container}>
                {this.renderHeader()}
                <TableWrapper store={this.props.store} onColumnSelectorVisible={onColumnSelectorVisible} />
                {/* Absolutely placed stuff that sort of belongs to the table */}
                {this.props.store.selectorTableStore.tableHeaderFilterMenu.isClosed ? null : <ColumnHeaderFilterMenu store={this.props.store} />}
                <CellDropdownList store={this.props.store} />
                {this.props.store.odtExportStore.reportModalIsOpen ? <ReportGenerationModal store={this.props.store} /> : null}
                <InsightsModalCellAttributeDrilldown store={this.props.store} />
            </div>
        );
    }
}

const tableCss = stylesheet({
    dataSourceSelector: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
    },
    dataSourceOpener: {
        cursor: 'pointer',
        paddingRight: 10,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    headerContainer: {
        borderTop: '1px solid ' + GreyColor,
        paddingTop: 5,
        paddingBottom: 5,
        borderTopLeftRadius: 20,
        borderLeft: '1px solid ' + GreyColor,
        marginLeft: -1,
        boxShadow: Shadow8,
    },
    headerContainerInner: {
        display: 'flex',
        height: 29,
        flexDirection: 'row',
    },
    colorLegend: {
        paddingLeft: 10,
        alignItems: 'center',
        display: 'flex',
        borderLeft: '1px solid ' + GreyColor,
        height: '100%',
    },
    tableTitle: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        marginRight: 10,
    },
    tableTitleText: {
        display: 'inline-block',
        marginLeft: 5,
    },
    channelSelectorColorLegendContainer: {
        paddingLeft: 10,
        display: 'flex',
        flexGrow: 1,
    },
    rightItemContainer: {
        position: 'relative',
        borderLeft: '1px solid ' + GreyColor,
        paddingLeft: 5,
        paddingRight: 5,
    },
    channelColorLegendChannelName: {
        display: 'inline-block',
        verticalAlign: 'top',
    },
    channelColorLegendBox: {
        opacity: 0.7,
        marginRight: 5,
        marginTop: 3,
        width: 20,
        height: 20,
        display: 'inline-block',
        verticalAlign: 'top',
    },
    channelColorLegendItem: {
        paddingRight: 10,
    },
    channelColorLegendTitle: {
        display: 'inline-block',
        verticalAlign: 'top',
        paddingRight: 15,
    },
    channelComparisonBar: {
        flex: 1,
        display: 'flex',
        minWidth: 0,
        position: 'relative',
        $nest: {
            '& > div': {
                whiteSpace: 'nowrap',
            },
        },
    },
    menuButton: {
        backgroundColor: 'transparent',
        border: 'none',
        width: 30,
        height: 29,
        cursor: 'pointer',
    },
    currencySelector: {
        height: '100%',
        border: 'none',
        backgroundColor: WhiteColor,
    },
    buttons: {
        display: 'inline-block',
        backgroundColor: WhiteColor,
        position: 'relative',
    },
});



// WEBPACK FOOTER //
// ./src/views/Table/SelectorTable.tsx